import { inject, Injectable } from "@angular/core";
import { Observable, of, delay } from "rxjs";
import { BackendApiService } from "../../../../app/services/backend-api.service";
import { BaseUrlConstantsService } from "../../../../app/services/base-url-constants.service";

export const SITES = [
  {
    id: "site_1",
    name: "Site Alpha",
    applications: [
      { id: "app_1", name: "Pumping Station" },
      { id: "app_2", name: "Conveyor Drive" },
    ],
  },
  {
    id: "site_2",
    name: "Site Beta",
    applications: [
      { id: "app_3", name: "Compressor Unit" },
      { id: "app_4", name: "Cooling Tower" },
    ],
  },
];

export const BEARING_NUMBERS = [
  "6206",
  "6208",
  "6306",
  "6308",
  "6310",
  "6312",
  "7206",
  "7208",
];

export const ODR_OPTIONS = [
    { value: "HIGH", name: "High(3400Hz)" },
    { value: "NORMAL", name: "Normal(1700Hz)" },
    { value: "LOW", name: "Low(200Hz)" },
    { value: "EXTRALOW", name: "Extra Low(100Hz)" },
    { value: "EXTRAEXTRALOW", name: "Extra Extra Low(50Hz)" },
    { value: "ULTRALOW", name: "Ultra Low" },
    { value: "ULTRAULTRALOW", name: "Ultra Ultra Low" },
  ];

export const ASSET_TYPES = [
  "MOTOR",
  "PUMP",
  "FAN_BLOWER",
  "COMPRESSOR",
  "GEAR",
  "JOURNAL_BEARING",
];

// master_type: 4 = Piezo, 1 = EM-Mini, 2 = EM-Standard, 3 = EM-Std + RTDs
export type MasterType = number;

export interface FormApiResponse {
  general_details: {
    asset_name: string;
    site: string;
    application: string;
  };
  configuration_details: {
    line_current: number;
    device_name: string;
    mac_address: number;
    motor_make: string;
    equipment_location: string;
    frame: string;
    rated_amb_temp: number;
    rated_amb_temp_unit: string;
    insulation_class: string;
    serial_number: string;
    rated_power: number;
    rated_rpm: number;
    rated_rpm_type: string;
    min_rpm?: number;
    max_rpm?: number;
    rated_voltage: number;
    percentage_eff: number;
    line_freq: string;
    power_factor: string;
    duty: string;
    bearing_type: string;
    de_number: string;
    nde_number: string;
    pole_pairs?: string;
    no_of_blades?: number;
    pinion_gear_details: any;
    output_gear_details: any;
    gmf_details: any;
  };
  drive_details: {
    drive_type: string;
    driver_pulley_diameter?: string;
    driven_pulley_diameter?: string;
    dist_bt_shaft?: string;
    belt_no?: string;
    belt_length?: string;
  };
  other_configurations: {
    sensor_mounting: string;
    RTD_1: string;
    RTD_2: string;
    RTD_3: string;
    temp_1_comp: string;
    temp_2_comp: string;
    temp_3_comp: string;
    vib_1_comp: string;
    vib_2_comp: string;
    vib_3_comp: string;
  };
  device_push_rate_configuration?: {
    summaryFileRefreshRate: number;
    fullRefreshFileRate: number;
    scanRate: number;
    odr_normal: string;
    odr_high_res: string;
    anomalyAnalysisTime: number;
    anomalyCounter: number;
    machine_threshold_status: number;
  };
  network_configuration?: {
    connectivity_type: string;
    wifi_ssid: string;
    wifi_password: string;
  };
}

@Injectable({ providedIn: "root" })
export class AssetConfigService {
  service = inject(BackendApiService);
  role=localStorage.getItem("role");

  /** Simulates an API call that returns pre-filled form data */
  fetchFormData(
    masterType: MasterType,
    subsystemId: string,
  ): Observable<FormApiResponse> {
    const data: FormApiResponse = {
      general_details: {
        asset_name: "Motor Unit Alpha",
        site: "1",
        application: "12",
      },
      configuration_details: {
        line_current: 12,
        device_name: masterType === 4 ? "PIEZO-DEV-001" : "EMS-DEV-001",
        mac_address: 7,
        motor_make: "ABB",
        equipment_location: "Plant Floor A - Bay 3",
        frame: "IEC 180",
        rated_amb_temp: 40,
        rated_amb_temp_unit: "C",
        insulation_class: "F",
        serial_number: masterType === 4 ? "PIEZO-SN-001" : "EMS-SN-001",
        rated_power: 75,
        rated_rpm: 1480,
        rated_rpm_type: "fixed",
        rated_voltage: 415,
        percentage_eff: 92,
        line_freq: "50",
        power_factor: "0.85",
        duty: "S1",
        bearing_type: "ball_bearing",
        de_number: "6308",
        nde_number: "6306",
        pole_pairs: "2",
        pinion_gear_details: {
          no_of_stages: "2",
          p_gear_rpm: "1480",
          p_gear_teeth: "32",
          p_bearing_type: "ball_bearing",
          p_deb_no: "6308",
          p_ndeb_no: "6306",
        },
        output_gear_details: {
          no_of_opt_gears: "1",
          o_gear_rpm: "740",
          o_gear_teeth: "64",
          o_bearing_type: "ball_bearing",
          o_deb_no: "6310",
          o_ndeb_no: "6312",
        },
        gmf_details: {
          is_gmf: "gmf_disabled",
          no_of_gmf: "",
          gmf_list: [],
        },
      },
      drive_details: {
        drive_type: "direct",
        driver_pulley_diameter: "",
        driven_pulley_diameter: "",
        dist_bt_shaft: "",
        belt_no: "",
        belt_length: "",
      },
      other_configurations: {
        sensor_mounting: "PARALLEL_SIDE",
        RTD_1: "MS",
        RTD_2: "na",
        RTD_3: "na",
        temp_1_comp: "0",
        temp_2_comp: "0",
        temp_3_comp: "0",
        vib_1_comp: "1",
        vib_2_comp: "1",
        vib_3_comp: "1",
      },
      // Only for EMsaver devices (master_type != 4)
      ...(masterType !== 4 && {
        device_push_rate_configuration: {
          summaryFileRefreshRate: 15,
          fullRefreshFileRate: 60,
          scanRate: 15,
          odr_normal: "Normal",
          odr_high_res: "Normal",
          anomalyAnalysisTime: 180,
          anomalyCounter: 1,
          machine_threshold_status: 0.5,
        },
        network_configuration: {
          connectivity_type: "Wifi",
          wifi_ssid: "MyNetwork",
          wifi_password: "",
        },
      }),
    };

    // Simulate network delay
    return of(data).pipe(delay(700));
  }

  async fetchSites(): Promise<any> {
    try {
      let client_id=null;
      if(this.role=='ADMIN')
        client_id=localStorage.getItem("admin_client_id");
      if(this.role=='USER')
        client_id=localStorage.getItem("associated_clientid");
      if(this.role=='CLIENT')
        client_id=localStorage.getItem("user_id");
      const sites_res: any = await this.service.getDataV2(
        `${BaseUrlConstantsService.BASE_MIDDLEWARE_URL}usersites/21${client_id}5025`,
      );
      console.log(sites_res);
      if (sites_res && sites_res.data && sites_res.data.body) {
        const sites = sites_res.data.body;
        console.log("Fetched sites:", sites);
        return sites;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      return [];
    }
  }

  async fetchInventory(): Promise<any> {
    try {
      let client_id=null;
      if(this.role=='ADMIN')
        client_id=localStorage.getItem("admin_client_id");
      if(this.role=='USER')
        client_id=localStorage.getItem("associated_clientid");
      if(this.role=='CLIENT')
        client_id=localStorage.getItem("user_id");
      const inventory_res: any = await this.service.getDataV2(
        `${BaseUrlConstantsService.BASE_MIDDLEWARE_URL}userinventory/57${client_id}2390`,
      );
      return inventory_res.data.body || [];
    } catch (error) {
      console.error("Error fetching inventory:", error);
      return [];
    }
  }

  async fetchBearingNumbers(): Promise<any> {
    try {
      const bearing_res: any = await this.service.getDataV2(
        `${BaseUrlConstantsService.ASSETS_BEARING}`,
      );
      return bearing_res.data.body || [];
    } catch (error) {
      console.error("Error fetching bearing numbers:", error);
      return [];
    }
  }

  async fetchFormDataV2({ sid, aid }): Promise<any> {
    try {
      const form_res: any = await this.service.getDataV2(
        `${BaseUrlConstantsService.BASE_PIEZO_SERVICE}/api/get_rotating_asset_form/${sid}/${aid}`,
      );
      return form_res.data || null;
    } catch (error) {}
  }

  async saveFormData(payload: any): Promise<any> {
    try {
      const save_res: any = await this.service.putData(
        `${BaseUrlConstantsService.BASE_PIEZO_SERVICE}/api/updated_rotating_asset_data/${payload.sensor_details.asset_id}`,
        payload,
      );
      return save_res || null;
    } catch (error) {
      console.error("Error saving form data:", error);
      return null;
    }
  }
}
